import type { Component, Characteristic, Value } from '../schema';
import type { ConfigentoStore } from '../state/store';
import type { CharacteristicSelection } from '../state/selections';

/**
 * Inverse of {@link buildAssetPayload}: take an alt_title-keyed asset JSON
 * (the format that the Shopify snapshot endpoint and the savebutton
 * actions consume) and seed the per-instance store with the corresponding
 * selections. Used by the Shopify-plugin loader to restore the
 * configurator into "edit" mode when the buyer clicks the cart-page
 * "Edit" link.
 *
 * Resolution per entry:
 *  - `text` / `area`           → `{ kind: 'text', value }`.
 *  - `select` / `radiobuttons` / `package`
 *      → prefer numeric `valueId` from the payload; fall back to matching
 *        `entry.value` against `Value.value` so a snapshot that was saved
 *        before our schema split (legacy → V3) still restores.
 *  - `multiselect` / `checkbox` / `listimage`
 *      → array of `valueId`s with the same fallback per entry.
 *  - `multiquantity`           → reconstruct `quantities` Record from the
 *        parallel `valueId`+`valueQty` arrays the encoder produces.
 *  - Calculated / render-only types are skipped — they're re-derived by
 *    the evaluator from the upstream selections.
 *
 * Every restored Characteristic is marked as `userTouched` (via
 * `setSelection`) so predefine rules don't overwrite the snapshot on the
 * next render pass.
 */
export function restoreFromAssetJson(
    component: Component,
    payload: Record<string, unknown>,
    store: ConfigentoStore,
): void {
    const { setSelection } = store.getState();

    for (const group of component.groups) {
        for (const c of group.characteristics) {
            if (!c.altTitle) continue;
            const raw = payload[c.altTitle];
            if (!raw || typeof raw !== 'object') continue;
            const entry = raw as Record<string, unknown>;
            const selection = entryToSelection(c, entry);
            if (selection) setSelection(c.id, selection);
        }
    }
}

function entryToSelection(
    c: Characteristic,
    entry: Record<string, unknown>,
): CharacteristicSelection | null {
    switch (c.type) {
        case 'text':
        case 'area': {
            const v = entry.value;
            if (typeof v !== 'string') return null;
            return { kind: 'text', value: v };
        }
        case 'select':
        case 'radiobuttons':
        case 'package': {
            const id = resolveValueId(c, entry.valueId, entry.value);
            // null is a valid restore target — it represents "explicitly
            // cleared" rather than "not yet picked", which differs from a
            // missing key in payload.
            const qty = firstQty(entry.valueQty);
            return qty === undefined ? { kind: 'single', valueId: id } : { kind: 'single', valueId: id, qty };
        }
        case 'multiselect':
        case 'checkbox': {
            const ids = resolveValueIdArray(c, entry.valueId, entry.value);
            const qtys = qtyMap(ids, entry.valueQty);
            return qtys ? { kind: 'multi', valueIds: ids, qtys } : { kind: 'multi', valueIds: ids };
        }
        case 'listimage': {
            // Single-pick listimage keeps its selection as `kind: 'single'`
            // in the store (mirrors select/radiobuttons); the UI's
            // `selectedIds` derivation reads different fields per pick
            // mode. `buildAssetPayload` always emits listimage as an
            // array-shape entry, so we look up the first id and route
            // it into the correct selection shape for this instance's
            // Characteristic type.
            const ids = resolveValueIdArray(c, entry.valueId, entry.value);
            if (c.isMultiple) {
                const qtys = qtyMap(ids, entry.valueQty);
                return qtys ? { kind: 'multi', valueIds: ids, qtys } : { kind: 'multi', valueIds: ids };
            }
            // Single-pick: take the first id, `null` when the payload
            // carries no selection at all (empty arrays are legal —
            // e.g. rule-driven blacklist wiped the pick).
            const qty = firstQty(entry.valueQty);
            const valueId = ids[0] ?? null;
            return qty === undefined ? { kind: 'single', valueId } : { kind: 'single', valueId, qty };
        }
        case 'multiquantity': {
            const ids = resolveValueIdArray(c, entry.valueId, entry.value);
            const qtys = Array.isArray(entry.valueQty) ? entry.valueQty : [];
            const quantities: Record<number, number> = {};
            for (let i = 0; i < ids.length; i++) {
                const qty = Number(qtys[i] ?? 1);
                if (Number.isFinite(qty) && qty > 0) {
                    quantities[ids[i]!] = qty;
                }
            }
            return { kind: 'quantities', quantities };
        }
        case 'fileupload': {
            // The asset payload carries the `files` array; restore it
            // so a re-visited configuration shows the previously
            // uploaded files without re-prompting the user. We don't
            // re-validate against the cloud here — the id+folder+file
            // tuple is what the recipient template needs, and if the
            // server has since pruned the file the next finalize call
            // will surface the issue.
            const rawFiles = entry.files;
            if (!Array.isArray(rawFiles)) return null;
            const restored = rawFiles
                .filter((f: unknown): f is Record<string, unknown> =>
                    typeof f === 'object' && f !== null)
                .map((f) => ({
                    id:           String(f.id ?? ''),
                    folder:       String(f.folder ?? ''),
                    type:         String(f.type ?? ''),
                    file:         String(f.file ?? ''),
                    originalName: String(f.originalName ?? f.file ?? ''),
                    size:         typeof f.size === 'number' ? f.size : 0,
                }));
            if (restored.length === 0) return null;
            return { kind: 'files', files: restored };
        }
        // Calculated / render-only types: skip restore. The evaluator and
        // pricing engine re-derive their state from upstream picks, and
        // savebutton / static / chart / unknown carry no selection state.
        // Captcha: the proof is single-use + bound to a fresh challenge,
        // so a restored value would be meaningless — start over.
        case 'expression':
        case 'combi':
        case 'matrixvalue':
        case 'productattribute':
        case 'webservice':
        case 'static':
        case 'chart':
        case 'savebutton':
        case 'captcha':
        case 'unknown':
            return null;
    }
}

function resolveValueId(
    c: Characteristic,
    rawId: unknown,
    rawValue: unknown,
): number | null {
    if (!('values' in c)) return null;
    const id = coerceNumber(rawId);
    if (id !== null && c.values.some((v) => v.id === id)) return id;
    if (typeof rawValue === 'string' && rawValue !== '') {
        const match = c.values.find((v: Value) => v.value === rawValue);
        if (match) return match.id;
    }
    return null;
}

function resolveValueIdArray(
    c: Characteristic,
    rawIds: unknown,
    rawValues: unknown,
): number[] {
    if (!('values' in c)) return [];
    const idsRaw = Array.isArray(rawIds) ? rawIds : [];
    const valsRaw = Array.isArray(rawValues) ? rawValues : [];
    const out: number[] = [];
    const max = Math.max(idsRaw.length, valsRaw.length);
    for (let i = 0; i < max; i++) {
        const id = coerceNumber(idsRaw[i]);
        if (id !== null && c.values.some((v) => v.id === id)) {
            out.push(id);
            continue;
        }
        const v = valsRaw[i];
        if (typeof v === 'string' && v !== '') {
            const match = c.values.find((cv: Value) => cv.value === v);
            if (match) out.push(match.id);
        }
    }
    return out;
}

function coerceNumber(v: unknown): number | null {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v !== '') {
        const n = Number(v);
        if (Number.isFinite(n)) return n;
    }
    return null;
}

/** `valueQty` of a single pick (a number, or the first of an array). */
function firstQty(raw: unknown): number | undefined {
    const n = Number(Array.isArray(raw) ? raw[0] : raw);
    return raw !== undefined && raw !== '' && Number.isFinite(n) && n > 0 ? n : undefined;
}

/** `valueQty` parallel to the picked ids, as the encoder writes it. */
function qtyMap(ids: ReadonlyArray<number>, raw: unknown): Record<number, number> | undefined {
    if (!Array.isArray(raw)) return undefined;
    const out: Record<number, number> = {};
    ids.forEach((id, i) => {
        const n = Number(raw[i]);
        if (Number.isFinite(n) && n > 0) out[id] = n;
    });
    return Object.keys(out).length > 0 ? out : undefined;
}
